import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { SxProps, Theme } from '@mui/material';
import { ArcAlert, ArcButton, ArcCircularProgress, ArcSkeleton, ArcText } from '@src/core/ui';
import type { FirmwareBannerProps, FirmwareBannerTone } from '../types';

const WARNING_ALERT_BG = '#FFF8E1';
const BANNER_SPINNER_SIZE = 20;
const ACTION_BUTTON_ICON_SIZE = 16;
/** Matches the loading skeleton; keeps height stable when the Update button is removed. */
const BANNER_MIN_HEIGHT = 50;

const actionButtonIconSx = {
  width: ACTION_BUTTON_ICON_SIZE,
  height: ACTION_BUTTON_ICON_SIZE,
  fontSize: ACTION_BUTTON_ICON_SIZE,
} as const;

const getActionButtonSx = (tone: FirmwareBannerTone, width: number): SxProps<Theme> => ({
  width,
  height: 24,
  minWidth: width,
  minHeight: 24,
  maxHeight: 24,
  boxShadow: 'none',
  fontWeight: 500,
  fontSize: 12,
  lineHeight: 1,
  textTransform: 'uppercase',
  borderRadius: '4px',
  px: 0.75,
  py: 0,
  '&:hover': { boxShadow: 'none' },
  '&.Mui-disabled': {
    bgcolor: `${tone}.light`,
    color: 'common.white',
    opacity: 1,
  },
  '& .MuiButton-startIcon': {
    mr: 0.5,
    ml: 0,
    color: 'inherit',
    width: ACTION_BUTTON_ICON_SIZE,
    height: ACTION_BUTTON_ICON_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': actionButtonIconSx,
    '& .MuiCircularProgress-root': {
      width: `${ACTION_BUTTON_ICON_SIZE}px !important`,
      height: `${ACTION_BUTTON_ICON_SIZE}px !important`,
    },
  },
});

const getAlertBg = (tone: FirmwareBannerTone): string =>
  tone === 'error' ? 'error.background' : WARNING_ALERT_BG;

const getAlertSx = (tone: FirmwareBannerTone): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: 1,
  minHeight: BANNER_MIN_HEIGHT,
  boxSizing: 'border-box',
  py: 1.5,
  px: 2,
  bgcolor: getAlertBg(tone),
  color: `${tone}.main`,
  '& .MuiAlert-icon': {
    color: `${tone}.main`,
    mr: 1.5,
    p: 0,
    py: 0,
    my: 0,
    alignSelf: 'center',
    alignItems: 'center',
    '& svg': { width: 24, height: 24, fontSize: 24 },
    '& .MuiCircularProgress-root': {
      width: `${BANNER_SPINNER_SIZE}px !important`,
      height: `${BANNER_SPINNER_SIZE}px !important`,
    },
    '& .MuiCircularProgress-svg': {
      width: BANNER_SPINNER_SIZE,
      height: BANNER_SPINNER_SIZE,
    },
  },
  '& .MuiAlert-message': {
    flex: 1,
    p: 0,
    py: 0,
    my: 0,
    alignSelf: 'center',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAlert-action': {
    alignSelf: 'center',
    alignItems: 'center',
    p: 0,
    m: 0,
    ml: 2,
    mr: 0,
  },
});

const FirmwareBanner: React.FC<FirmwareBannerProps> = ({
  view,
  onUpdate,
  isUpdating,
  isLoading,
}) => {
  if (isLoading) return <ArcSkeleton width='100%' height={BANNER_MIN_HEIGHT} />;
  if (!view.visible) return null;

  const { tone, message, showSpinner, showUpdateButton } = view;
  const buttonLabel = 'Update';
  const showButton = showUpdateButton;
  const buttonWidth = 89;

  const icon = showSpinner ? (
    <ArcCircularProgress
      size={BANNER_SPINNER_SIZE}
      thickness={4}
      sx={{ color: `${tone}.main` }}
    />
  ) : (
    <WarningAmberIcon sx={{ width: 24, height: 24, fontSize: 24 }} />
  );

  const action = !showButton ? undefined : (
    <ArcButton
      variant='contained'
      color={tone}
      startIcon={
        isUpdating ? (
          <ArcCircularProgress
            size={ACTION_BUTTON_ICON_SIZE}
            thickness={4}
            sx={{ ...actionButtonIconSx, color: 'common.white' }}
          />
        ) : (
          <RefreshIcon sx={actionButtonIconSx} />
        )
      }
      onClick={onUpdate}
      disabled={isUpdating}
      sx={getActionButtonSx(tone, buttonWidth)}
    >
      {buttonLabel}
    </ArcButton>
  );

  return (
    <ArcAlert
      severity={tone}
      icon={icon}
      sx={getAlertSx(tone)}
      action={action}
    >
      <ArcText.h5
        color={`${tone}.main`}
        fontWeight={400}
        fontSize={16}
        lineHeight='100%'
        letterSpacing={0}
      >
        {message}
      </ArcText.h5>
    </ArcAlert>
  );
};

export default FirmwareBanner;
